import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FileUpload from '../components/FileUpload';

const API_BASE_URL = 'https://myreadyroom-api-crchhghhctcvgagq.westus2-01.azurewebsites.net';

export default function PresentationSetup() {
  const navigate = useNavigate();
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('board');
  const [outline, setOutline] = useState('');
  const [sessionLength, setSessionLength] = useState(15);
  const [allowInterruptions, setAllowInterruptions] = useState(false);
  const [halfwayFeedback, setHalfwayFeedback] = useState(true);
  const [confidenceScoring, setConfidenceScoring] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const lengths = [5, 10, 15, 20, 30];

  const audiences = [
    { id: 'board', label: 'Board / Exec Committee', icon: '🏛️', desc: 'Skeptical, ROI-focused, short on time' },
    { id: 'investors', label: 'Investors / VC', icon: '💰', desc: 'Market size, competition, traction' },
    { id: 'exec_team', label: 'Executive Team', icon: '👔', desc: 'Feasibility, resources, alignment' },
    { id: 'all_hands', label: 'All-Hands / Town Hall', icon: '🏢', desc: 'Employees asking about impact and culture' },
    { id: 'clients', label: 'Clients / Customers', icon: '🤝', desc: 'Value, trust, ROI, implementation' },
  ];

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topic || !outline) {
      setError('Please enter your topic and talking points');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/sessions/prepare-presentation`,
        {
          topic,
          audienceType: audience,
          outline,
          sessionLengthMinutes: sessionLength,
          allowInterruptions,
          halfwayFeedback,
          confidenceScoring,
          sessionType: 'presentation'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/live-presentation', { state: { session: response.data } });
    } catch (err: any) {
      if (err.response?.data?.upgradeRequired) {
        if (window.confirm(err.response.data.error + '\n\nUpgrade to Pro now?')) {
          const token = localStorage.getItem('token');
          const res = await axios.post(`${API_BASE_URL}/api/stripe/create-checkout`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          window.location.href = res.data.checkoutUrl;
        }
      } else {
        setError('Failed to prepare session. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <p style={styles.brand}>VIRILITER</p>
        <h1 style={styles.title}>Presentation Prep</h1>
        <p style={styles.subtitle}>Practice with a live AI audience that challenges you.</p>

        <form onSubmit={handleStart} style={styles.form}>
          {error && <p style={styles.error}>{error}</p>}

          <div style={styles.section}>
            <p style={styles.label}>PRESENTATION TOPIC</p>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g. Q3 Security Roadmap, Series A Pitch, Digital Transformation Strategy"
              value={topic}
              onChange={e => setTopic(e.target.value)}
            />
          </div>

          <div style={styles.section}>
            <p style={styles.label}>YOUR AUDIENCE</p>
            <div style={styles.audienceGrid}>
              {audiences.map(a => (
                <button
                  key={a.id}
                  type="button"
                  style={{ ...styles.audienceButton, ...(audience === a.id ? styles.audienceActive : {}) }}
                  onClick={() => setAudience(a.id)}
                >
                  <span style={styles.audienceIcon}>{a.icon}</span>
                  <span style={{ ...styles.audienceLabel, color: audience === a.id ? '#F1F5F9' : '#94A3B8' }}>{a.label}</span>
                  <span style={styles.audienceDesc}>{a.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={styles.section}>
            <p style={styles.label}>TALKING POINTS / OUTLINE</p>
            <FileUpload onExtracted={(text) => setOutline(text)} fieldName="Presentation Outline" />
            <textarea
              style={styles.textarea}
              placeholder="Paste your slide outline, talking points, or key messages here, or upload above..."
              value={outline}
              onChange={e => setOutline(e.target.value)}
              rows={6}
            />
          </div>

          <div style={styles.section}>
            <p style={styles.label}>SESSION LENGTH</p>
            <div style={styles.lengths}>
              {lengths.map(l => (
                <button
                  key={l}
                  type="button"
                  style={{ ...styles.lengthButton, ...(sessionLength === l ? styles.lengthActive : {}) }}
                  onClick={() => setSessionLength(l)}
                >
                  <span style={{ color: sessionLength === l ? '#ffffff' : '#475569', fontWeight: '700' }}>{l}m</span>
                </button>
              ))}
            </div>
          </div>

          <div style={styles.section}>
            <p style={styles.label}>AUDIENCE SETTINGS</p>
            <div style={styles.settingsCard}>
              <label style={styles.toggle}>
                <div style={styles.toggleLeft}>
                  <p style={styles.toggleTitle}>Allow audience interruptions</p>
                  <p style={styles.toggleDesc}>Audience can ask questions during your presentation</p>
                </div>
                <input
                  type="checkbox"
                  checked={allowInterruptions}
                  onChange={e => setAllowInterruptions(e.target.checked)}
                  style={styles.checkbox}
                />
              </label>
              <div style={styles.divider} />
              <label style={styles.toggle}>
                <div style={styles.toggleLeft}>
                  <p style={styles.toggleTitle}>Halfway point feedback</p>
                  <p style={styles.toggleDesc}>Pause at 50% for coaching — adjust, restart, or continue</p>
                </div>
                <input
                  type="checkbox"
                  checked={halfwayFeedback}
                  onChange={e => setHalfwayFeedback(e.target.checked)}
                  style={styles.checkbox}
                />
              </label>
              <div style={styles.divider} />
              <label style={styles.toggle}>
                <div style={styles.toggleLeft}>
                  <p style={styles.toggleTitle}>Confidence & pacing scoring</p>
                  <p style={styles.toggleDesc}>Score filler words, pace, and vocal confidence</p>
                </div>
                <input
                  type="checkbox"
                  checked={confidenceScoring}
                  onChange={e => setConfidenceScoring(e.target.checked)}
                  style={styles.checkbox}
                />
              </label>
            </div>
          </div>

          <div style={styles.disclaimerBox}>
            <p style={styles.disclaimerTitle}>⚠ DISCLAIMER</p>
            <p style={styles.disclaimerText}>
              This is an AI-powered simulation. The audience is not affiliated with any real organization. This session is for practice purposes only.
            </p>
          </div>

          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? 'Preparing your audience...' : 'Start Presentation Prep'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#0A0F1E', display: 'flex', justifyContent: 'center', padding: '40px 24px' },
  card: { width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '16px' },
  brand: { color: '#3B82F6', fontSize: '11px', letterSpacing: '6px', fontWeight: '700', textAlign: 'center', margin: 0 },
  title: { color: '#F1F5F9', fontSize: '30px', fontWeight: '800', textAlign: 'center', margin: 0, letterSpacing: '-0.5px' },
  subtitle: { color: '#475569', fontSize: '13px', textAlign: 'center', margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '8px' },
  section: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { color: '#3B82F6', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', margin: 0 },
  error: { color: '#EF4444', fontSize: '14px', margin: 0 },
  input: { backgroundColor: '#0F172A', color: '#F1F5F9', padding: '16px', borderRadius: '12px', fontSize: '16px', border: '1px solid #1E293B', outline: 'none', width: '100%', boxSizing: 'border-box' },
  textarea: { backgroundColor: '#0F172A', color: '#F1F5F9', padding: '16px', borderRadius: '12px', fontSize: '15px', border: '1px solid #1E293B', outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
  audienceGrid: { display: 'flex', flexDirection: 'column', gap: '8px' },
  audienceButton: { backgroundColor: '#0F172A', border: '1px solid #1E293B', borderRadius: '12px', padding: '14px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '12px' },
  audienceActive: { borderColor: '#3B82F6', backgroundColor: '#0D1F3C' },
  audienceIcon: { fontSize: '20px', flexShrink: 0 },
  audienceLabel: { fontSize: '14px', fontWeight: '600', flex: 1 },
  audienceDesc: { color: '#334155', fontSize: '12px', flexShrink: 0 },
  lengths: { display: 'flex', gap: '8px' },
  lengthButton: { flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #1E293B', backgroundColor: '#0F172A', cursor: 'pointer' },
  lengthActive: { backgroundColor: '#3B82F6', borderColor: '#3B82F6' },
  settingsCard: { backgroundColor: '#0F172A', borderRadius: '12px', border: '1px solid #1E293B', overflow: 'hidden' },
  toggle: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', cursor: 'pointer' },
  toggleLeft: { flex: 1 },
  toggleTitle: { color: '#F1F5F9', fontSize: '14px', fontWeight: '600', margin: 0 },
  toggleDesc: { color: '#475569', fontSize: '12px', margin: '2px 0 0 0', lineHeight: '1.5' },
  checkbox: { width: '18px', height: '18px', accentColor: '#3B82F6', flexShrink: 0, cursor: 'pointer' },
  divider: { height: '1px', backgroundColor: '#1E293B', margin: '0 16px' },
  disclaimerBox: { backgroundColor: '#0F172A', borderRadius: '12px', padding: '16px', border: '1px solid #1E293B' },
  disclaimerTitle: { color: '#F59E0B', fontSize: '11px', fontWeight: '700', letterSpacing: '2px', margin: '0 0 6px 0' },
  disclaimerText: { color: '#475569', fontSize: '13px', lineHeight: '1.6', margin: 0 },
  button: { backgroundColor: '#3B82F6', color: '#ffffff', padding: '20px', borderRadius: '12px', fontSize: '17px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
};